/**
 * The island, from above.
 *
 * Editing a world a screen at a time makes it easy to lose track of the shape of
 * the whole thing: where the coast is, where the woods are, whether the zones
 * are spread out or piled up in one corner. This draws the region as it is,
 * marks what has been placed on it, and takes the view wherever it is clicked.
 *
 * Drawn from the same data the world is: the ground the client already has, so
 * it costs nothing to keep and is never out of step with what is on screen.
 */

import type { SpawnWire, ZoneWire } from '@inverse/protocol';
import type { LocalRegion } from '../../game/world-state';
import { useEffect, useRef, useState } from 'react';
import { paintRegion, tileOnMap } from '../map-paint';

/**
 * How big the picture is, at most, in pixels each way.
 *
 * It used to be two pixels to the tile, which on the island it was written for
 * was a picture of two hundred and fifty six pixels and on a continent is one
 * of four thousand: sixteen million pixels, redrawn every time the character
 * moved. The panel it sits in is a few hundred pixels wide, so anything past
 * that is work nobody can see.
 */
const MOST = 640;

export function EditorMap({
  region,
  zones,
  spawns,
  character,
  focus,
  held,
  repaint = 0,
  onGo,
}: {
  region: LocalRegion | null;
  zones: readonly ZoneWire[];
  spawns: readonly SpawnWire[];
  /** Where the character is standing, in tiles. */
  character: { tx: number; tz: number } | null;
  /** Where the view is looking, when that is somewhere else. */
  focus: { tx: number; tz: number } | null;
  /** What the editor is holding, so it can be found again from above. */
  held: readonly { tx: number; tz: number }[];
  /** Bumped to paint the ground again, for when it has been edited. */
  repaint?: number;
  onGo: (tx: number, tz: number) => void;
}): JSX.Element {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [where, setWhere] = useState('');

  /**
   * The ground itself, painted once and kept.
   *
   * This used to be repainted every time anything on it moved, and what moves
   * on it is the character, once a second. Painting a continent is four million
   * pixels: the panel dropped a frame every second it was open, which is what
   * made the tools feel broken while it was.
   *
   * The ground only changes when somebody edits it, so it is painted when the
   * region arrives or when the button below asks, and everything that moves is
   * drawn over the top of it.
   */
  const ground = useRef<HTMLCanvasElement | null>(null);
  const paintedFor = useRef('');
  const paintedSquares = useRef(-1);
  const paintedAt = useRef(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !region) return;

    const context = canvas.getContext('2d');
    if (!context) return;

    const scale = Math.min(2, MOST / Math.max(region.width, region.height));
    canvas.width = Math.max(1, Math.round(region.width * scale));
    canvas.height = Math.max(1, Math.round(region.height * scale));

    // The same picture the minimap and the world map are drawn from, scaled up
    // with smoothing off. One answer to what colour a shore is.
    context.imageSmoothingEnabled = false;

    // Painting a continent is four million pixels, so it is done when the place
    // changes or when something is built, and otherwise at most once every few
    // seconds however much ground has arrived. Ground streams in a square at a
    // time as somebody moves, and repainting the whole continent for each of
    // them is what made this panel drop a frame every second it was open.
    const key = `${region.id}.${region.width}.${region.height}.${repaint}`;
    const now = Date.now();
    const grown = region.squares.size !== paintedSquares.current;

    if (
      !ground.current ||
      paintedFor.current !== key ||
      (grown && now - paintedAt.current > 4000)
    ) {
      ground.current = paintRegion(region).canvas;
      paintedFor.current = key;
      paintedSquares.current = region.squares.size;
      paintedAt.current = now;
    }

    context.drawImage(ground.current, 0, 0, canvas.width, canvas.height);

    const place = (tx: number, tz: number): { x: number; y: number } => {
      const at = tileOnMap(region, tx, tz);
      return { x: at.x * scale, y: at.y * scale };
    };

    for (const zone of zones) {
      context.fillStyle = `hsla(${(zone.id * 47) % 360}, 70%, 62%, 0.55)`;
      for (const tile of zone.tiles) {
        const at = place(tile.tx, tile.tz);
        context.fillRect(at.x, at.y, Math.max(1, scale), Math.max(1, scale));
      }
    }

    for (const spawn of spawns) {
      const at = place(spawn.tx, spawn.tz);
      context.fillStyle = spawn.alive ? '#a8e8ef' : '#8a9296';
      context.fillRect(at.x - 1, at.y - 1, Math.max(1, scale) + 2, Math.max(1, scale) + 2);
    }

    // What is held, drawn over everything else: finding the wall you are working
    // on is most of what the map is for once a world gets big.
    context.fillStyle = '#a8f0f7';
    for (const tile of held.slice(0, 2048)) {
      const at = place(tile.tx, tile.tz);
      context.fillRect(at.x, at.y, Math.max(1, scale), Math.max(1, scale));
    }

    if (focus) {
      const at = place(focus.tx, focus.tz);
      context.strokeStyle = '#c8a86a';
      context.lineWidth = 1;
      context.strokeRect(at.x - 4, at.y - 4, 9, 9);
    }

    if (character) {
      const at = place(character.tx, character.tz);
      context.fillStyle = '#ffffff';
      context.fillRect(at.x - 1, at.y - 1, Math.max(1, scale) + 2, Math.max(1, scale) + 2);
    }
  }, [region, zones, spawns, character, focus, held, repaint]);

  return (
    <>
      {/* Somewhere written down in notes rather than somewhere on the map: a
          designer working from a list of places should not have to hunt for
          them with the pointer. */}
      <form
        className="editor-goto"
        onSubmit={(event) => {
          event.preventDefault();

          const found = /(-?\d+)\D+(-?\d+)/.exec(where);
          const tx = Number(found?.[1] ?? Number.NaN);
          const tz = Number(found?.[2] ?? Number.NaN);
          if (!Number.isFinite(tx) || !Number.isFinite(tz)) return;

          onGo(tx, tz);
          setWhere('');
        }}
      >
        <input
          className="editor-search"
          type="text"
          value={where}
          placeholder="Go to 3300, 3250"
          onChange={(event) => setWhere(event.target.value)}
        />
      </form>

      <div className="editor-map-frame">
        <canvas
          ref={canvasRef}
          className="editor-map"
          onClick={(event) => {
            const canvas = canvasRef.current;
            if (!canvas || !region) return;

            // North is up and east is to the right, in the picture and in the
            // numbers alike, so neither axis is turned round here. This used to
            // flip the second one, left over from a picture that was drawn
            // upside down, and every click landed on the far coast.
            const box = canvas.getBoundingClientRect();
            const tx =
              region.originX + Math.floor(((event.clientX - box.left) / box.width) * region.width);
            const tz =
              region.originZ + Math.floor(((event.clientY - box.top) / box.height) * region.height);

            onGo(tx, tz);
          }}
        />
      </div>
    </>
  );
}
